import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { ScalesIcon as Scales, ArrowLeftIcon as ArrowLeft, FilePdfIcon as FilePdf, FileTextIcon as FileText, WarningIcon as Warning } from "@phosphor-icons/react";

/**
 * Admin → IP Portfolio Audit.
 *
 * Renders a structured, sharable inventory of every protectable
 * asset on the platform (word marks, design marks, copyrightable
 * content, source code, etc.), pulled from the backend at
 * /api/admin/ip-portfolio. Two download formats hand off cleanly to
 * the user's IP counsel:
 *   - Markdown — emailable
 *   - PDF — printable / archivable
 *
 * The inventory is generated from a Python data file
 * (`/app/backend/services/ip_portfolio_data.py`) so the page is
 * always in sync with what ships on the platform.
 *
 * Prominent "NOT LEGAL ADVICE" framing throughout — counsel makes
 * the call on which assets to file, in which class, in which
 * jurisdiction.
 */
export default function AdminIPPortfolio() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/admin/ip-portfolio")
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, []);

  // Authenticated downloads — we hand the user a blob URL because
  // a plain <a href> won't carry the Authorization header. Same
  // approach used elsewhere in the platform (executed-PDFs).
  const downloadAuthenticated = async (path, filename) => {
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${filename} downloaded`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (error) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto px-4 py-10 text-crimson" data-testid="ip-portfolio-error">
          {error}
        </div>
      </AppShell>
    );
  }
  if (!data) {
    return <AppShell><div className="max-w-3xl mx-auto px-4 py-10">Loading…</div></AppShell>;
  }

  const todayStr = data.generated_at;
  const totalItems = (data.categories || []).reduce(
    (sum, c) => sum + (c.items?.length || 0), 0,
  );

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 pb-16" data-testid="admin-ip-portfolio">
        <header className="mb-6">
          <Link to="/admin" className="text-[12px] text-[#6B6B6B] hover:text-ink mb-2 inline-flex items-center gap-1">
            <ArrowLeft size={12} weight="bold" /> Back to admin
          </Link>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2">For counsel</p>
              <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight flex items-center gap-2">
                <Scales size={28} weight="duotone" /> IP Portfolio Audit
              </h1>
              <p className="text-[13px] text-[#6B6B6B] mt-2 max-w-2xl leading-relaxed">
                Compiled inventory of trademark- and copyright-protectable
                assets shipped on the platform. {totalItems} items across {data.categories.length} categories.
                Generated {todayStr}.
              </p>
              <p className="text-[11px] text-[#9A9A9A] mt-2" data-testid="ip-portfolio-priority-legend">
                <span className="font-bold text-crimson">P0</span> file first (core brand + active products)
                &nbsp;·&nbsp; <span className="font-bold text-gold">P1</span> next filing round
                &nbsp;·&nbsp; <span className="font-bold">P2</span> later / monitor
              </p>
            </div>
            <div className="flex flex-col gap-2 flex-shrink-0">
              <Button
                onClick={() => downloadAuthenticated("/admin/ip-portfolio/export.pdf", `armstrong-k9-ip-portfolio-${todayStr}.pdf`)}
                className="rounded-full bg-ink hover:bg-crimson text-white"
                data-testid="ip-portfolio-download-pdf"
              >
                <FilePdf size={14} weight="bold" className="mr-1" />
                Download PDF
              </Button>
              <Button
                onClick={() => downloadAuthenticated("/admin/ip-portfolio/export.md", `armstrong-k9-ip-portfolio-${todayStr}.md`)}
                variant="outline"
                className="rounded-full"
                data-testid="ip-portfolio-download-md"
              >
                <FileText size={14} weight="bold" className="mr-1" />
                Download Markdown
              </Button>
            </div>
          </div>
        </header>

        <DisclaimerBanner text={data.disclaimer} />

        {data.tm_filing_package && <FilingPackage pkg={data.tm_filing_package} />}

        <div className="space-y-10 mt-8">
          <h2 className="font-spectral text-2xl tracking-tight text-ink pt-4 border-t-2 border-ink/10" data-testid="ip-part2-heading">
            Part 2 — Full IP inventory
          </h2>
          {data.categories.map((cat) => (
            <CategorySection key={cat.id} cat={cat} />
          ))}
        </div>

        <footer className="mt-12 pt-6 border-t border-[#EAEAEA]">
          <p className="text-[11.5px] text-[#9A9A9A] italic leading-relaxed max-w-2xl">
            Inventory generated from{" "}
            <span className="font-mono text-[10.5px] text-[#6B6B6B]">/app/backend/services/ip_portfolio_data.py</span>.
            When new branded products or copyrightable assets ship, edit that file and the audit will refresh on the next load.
          </p>
        </footer>
      </div>
    </AppShell>
  );
}

function DisclaimerBanner({ text }) {
  return (
    <aside
      className="rounded-2xl border-2 border-[#C1272D]/30 bg-[#C1272D]/5 px-5 py-4 flex items-start gap-3"
      data-testid="ip-portfolio-disclaimer"
    >
      <Warning size={20} weight="fill" className="text-[#C1272D] flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#C1272D] mb-1">NOT LEGAL ADVICE</p>
        <p className="text-[12.5px] text-[#3a3a3a] leading-relaxed">{text}</p>
      </div>
    </aside>
  );
}

function CategorySection({ cat }) {
  return (
    <section data-testid={`ip-category-${cat.id}`} className="scroll-mt-20">
      <h2 className="font-spectral text-2xl tracking-tight text-ink mb-2">{cat.title}</h2>
      {cat.intro && (
        <p className="text-[12.5px] text-[#6B6B6B] leading-relaxed mb-5 max-w-3xl">{cat.intro}</p>
      )}
      <div className="space-y-3">
        {cat.items.map((item, idx) => (
          <ItemCard key={`${cat.id}-${idx}`} item={item} />
        ))}
      </div>
    </section>
  );
}

function FilingPackage({ pkg }) {
  const a = pkg.applicant;
  return (
    <section className="mt-8" data-testid="ip-filing-package">
      <h2 className="font-spectral text-2xl tracking-tight text-ink mb-2">
        Part 1 — Trademark filing package
      </h2>
      <p className="text-[12.5px] text-[#6B6B6B] leading-relaxed mb-5 max-w-3xl">
        Lawyer-ready worksheets: applicant details, filing basis, first-use
        dates, suggested Nice classes with draft goods/services language,
        specimen guidance, and clearance flags per mark. Class suggestions are
        starting points — counsel maps them to the USPTO ID Manual.
      </p>

      <div className="rounded-xl border border-[#EAEAEA] bg-white p-4 sm:p-5 mb-4" data-testid="ip-applicant-card">
        <h3 className="font-bold text-ink text-[13px] uppercase tracking-wider mb-3">Applicant</h3>
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[12.5px]">
          {[
            ["Legal name", a.legal_name],
            ["Entity type", a.entity_type],
            ["Address", a.address],
            ["Citizenship", a.citizenship],
            ["Website", a.website],
            ["Correspondence", a.correspondence],
          ].map(([k, v]) => (
            <div key={k} className="flex gap-2">
              <dt className="font-bold uppercase tracking-wider text-[10px] text-[#6B6B6B] flex-shrink-0 w-24 pt-0.5">{k}</dt>
              <dd className="text-[#3a3a3a] leading-relaxed min-w-0">{v}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="rounded-xl border border-gold/40 bg-gold/5 p-4 sm:p-5 mb-6">
        <h3 className="font-bold text-ink text-[13px] uppercase tracking-wider mb-2">Filing notes for counsel</h3>
        <ul className="list-disc pl-4 space-y-1.5 text-[12px] text-[#3a3a3a] leading-relaxed">
          {pkg.filing_notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      </div>

      <div className="space-y-4">
        {pkg.worksheets.map((w) => (
          <article
            key={w.mark}
            className="rounded-xl border border-[#EAEAEA] bg-white p-4 sm:p-5"
            data-testid="ip-tm-worksheet"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <h3 className="font-bold text-ink text-[14px] leading-tight">{w.mark}</h3>
              <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wider ${PRIORITY_STYLES[w.priority] || PRIORITY_STYLES.P2}`}>
                {w.priority}
              </span>
            </div>
            <dl className="mt-3 space-y-2 text-[12.5px]">
              {[
                ["Mark type", w.mark_type],
                ["Basis", w.filing_basis],
                ["First use", w.first_use],
                ["Specimen", w.specimen],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-2">
                  <dt className="font-bold uppercase tracking-wider text-[10px] text-[#6B6B6B] flex-shrink-0 w-20 pt-0.5">{k}</dt>
                  <dd className="text-[#3a3a3a] leading-relaxed">{v}</dd>
                </div>
              ))}
              <div className="flex gap-2">
                <dt className="font-bold uppercase tracking-wider text-[10px] text-[#6B6B6B] flex-shrink-0 w-20 pt-0.5">Classes</dt>
                <dd className="text-[#3a3a3a] leading-relaxed">
                  <ul className="space-y-1">
                    {w.classes.map((c) => (
                      <li key={c.nice_class}>
                        <span className="font-bold">{c.nice_class}</span> — {c.description}
                      </li>
                    ))}
                  </ul>
                </dd>
              </div>
            </dl>
            <div className="mt-3 pt-3 border-t border-[#F1ECDD]">
              <p className="text-[11.5px] italic text-crimson leading-relaxed">
                <span className="not-italic font-bold uppercase tracking-wider text-[9.5px] text-crimson mr-1">Clearance:</span>
                {w.clearance}
              </p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

const PRIORITY_STYLES = {
  P0: "bg-crimson/10 text-crimson border-crimson/30",
  P1: "bg-gold/10 text-[#8A6A00] border-gold/40",
  P2: "bg-[#F4F4F4] text-[#6B6B6B] border-[#E0E0E0]",
};

function ItemCard({ item }) {
  return (
    <article className="rounded-xl border border-[#EAEAEA] bg-white p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h3 className="font-bold text-ink text-[14px] leading-tight min-w-0">{item.name}</h3>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {item.priority && (
            <span
              className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wider ${PRIORITY_STYLES[item.priority] || PRIORITY_STYLES.P2}`}
              data-testid="ip-item-priority"
            >
              {item.priority}
            </span>
          )}
          {item.status && (
            <span
              className="inline-block rounded-full border border-[#E0E0E0] bg-[#FAFAF7] px-2 py-0.5 text-[10px] text-[#6B6B6B]"
              data-testid="ip-item-status"
            >
              {item.status}
            </span>
          )}
        </div>
      </div>
      <dl className="mt-3 space-y-2 text-[12.5px]">
        <div className="flex gap-2">
          <dt className="font-bold uppercase tracking-wider text-[10px] text-[#6B6B6B] flex-shrink-0 w-20 pt-0.5">Where</dt>
          <dd className="text-[#3a3a3a] leading-relaxed">{item.where}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="font-bold uppercase tracking-wider text-[10px] text-[#6B6B6B] flex-shrink-0 w-20 pt-0.5">Notes</dt>
          <dd className="text-[#3a3a3a] leading-relaxed">{item.notes}</dd>
        </div>
      </dl>
      <div className="mt-3 pt-3 border-t border-[#F1ECDD]">
        <p className="text-[11.5px] italic text-crimson leading-relaxed">
          <span className="not-italic font-bold uppercase tracking-wider text-[9.5px] text-crimson mr-1">Filing direction:</span>
          {item.suggestion}
          <span className="text-[#9A9A9A] not-italic"> &nbsp;·&nbsp; counsel decides</span>
        </p>
      </div>
    </article>
  );
}
